
import {
  defineComponent,
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  watch,
} from 'vue'
import moment from 'moment'
import { useStarUserPermission } from '@/utils/useStarUserPermission'

const defaultAvatar = require('@/assets/star/logo.png')
const PANEL_WIDTH = 400
const PANEL_GAP = 8

function formatQuota(remain?: number, total?: number, notLimit?: boolean) {
  if (notLimit) return '无限'
  if (remain == null && total == null) return '--'
  return `${remain ?? '--'}/${total ?? '--'}`
}

export default defineComponent({
  name: 'StarUserProfile',
  setup() {
    const visible = ref(false)
    const triggerRef = ref<HTMLElement | null>(null)
    const panelRef = ref<HTMLElement | null>(null)
    const panelStyle = ref<Record<string, string>>({})
    const { permission, refreshPermission } = useStarUserPermission()

    const avatarUrl = computed(() => permission.value?.headImage || defaultAvatar)

    const starLevelText = computed(() => {
      const p = permission.value
      if (!p) return '--'
      if (p.starName) return p.starName
      if (p.starLevel != null && p.starLevel > 0) return `${p.starLevel}星`
      return '免费用户'
    })

    const expireText = computed(() => {
      const ts = permission.value?.expireTime
      if (!ts) return ''
      return moment(ts).format('YYYY-MM-DD')
    })

    const testNumText = computed(() =>
      formatQuota(
        permission.value?.testNumRemain,
        permission.value?.testNumTotal,
        permission.value?.testNumNotLimit
      )
    )

    const saveNumText = computed(() =>
      formatQuota(
        permission.value?.saveNumRemain,
        permission.value?.saveNumTotal,
        permission.value?.saveNumNotLimit
      )
    )

    const subNumText = computed(() =>
      formatQuota(
        permission.value?.subNumRemain,
        permission.value?.subNumTotal,
        permission.value?.subNumNotLimit
      )
    )

    const templateLockText = computed(() => {
      const val = permission.value?.starNameTemplateLock
      if (val == null || val === '') return '--'
      return String(val)
    })

    const updatePanelPosition = () => {
      const trigger = triggerRef.value
      if (!trigger) return

      const rect = trigger.getBoundingClientRect()
      let left = rect.right - PANEL_WIDTH
      const top = rect.bottom + PANEL_GAP

      left = Math.max(8, Math.min(left, window.innerWidth - PANEL_WIDTH - 8))

      panelStyle.value = {
        position: 'fixed',
        top: `${top}px`,
        left: `${left}px`,
        width: `${PANEL_WIDTH}px`,
        zIndex: '1050',
      }
    }

    const closePanel = () => {
      visible.value = false
    }

    const openPanel = async () => {
      visible.value = true
      await refreshPermission()
      await nextTick()
      updatePanelPosition()
    }

    const togglePanel = () => {
      if (visible.value) {
        closePanel()
      } else {
        void openPanel()
      }
    }

    const onDocumentClick = (event: MouseEvent) => {
      if (!visible.value) return
      const target = event.target as Node
      if (triggerRef.value?.contains(target)) return
      if (panelRef.value?.contains(target)) return
      closePanel()
    }

    watch(visible, (val) => {
      if (val) {
        nextTick(updatePanelPosition)
        window.addEventListener('resize', updatePanelPosition)
        window.addEventListener('scroll', updatePanelPosition, true)
      } else {
        window.removeEventListener('resize', updatePanelPosition)
        window.removeEventListener('scroll', updatePanelPosition, true)
      }
    })

    onMounted(() => {
      document.addEventListener('click', onDocumentClick)
    })

    onBeforeUnmount(() => {
      document.removeEventListener('click', onDocumentClick)
      window.removeEventListener('resize', updatePanelPosition)
      window.removeEventListener('scroll', updatePanelPosition, true)
    })

    return {
      visible,
      triggerRef,
      panelRef,
      panelStyle,
      togglePanel,
      avatarUrl,
      starLevelText,
      expireText,
      testNumText,
      saveNumText,
      subNumText,
      templateLockText,
    }
  },
})
